/**
 * Digital Marketing — top-level sidebar group.
 *
 * Per-platform visibility is RBAC-driven. JWTContext fetches /api/ai/me on
 * app boot and writes the user's allowed platforms array to localStorage as
 * `dmPlatforms`. We read it here at module-init time, same as the old
 * phoneNumber pattern. Empty / missing = no DM section in the sidebar.
 *
 * Backend RBAC config lives at:
 *   manijment-api/services/ai-service/dashboardAccess.js
 */

import React from "react";
import { FormattedMessage } from "react-intl";
import {
  IconChartBar,
  IconShoppingCart,
  IconBrandShopee,
  IconBrandAndroid,
  IconBrandStripe,
  IconFileUpload,
  IconStar,
  IconUsers,
  IconHeartbeat,
  IconTarget,
} from "@tabler/icons";

const icons = { IconChartBar, IconShoppingCart, IconBrandShopee, IconBrandAndroid, IconBrandStripe, IconFileUpload, IconStar, IconUsers, IconHeartbeat, IconTarget };

// Read RBAC-allowed platforms from localStorage (set by JWTContext on boot).
function getAllowedPlatforms() {
  try { return JSON.parse(localStorage.getItem("dmPlatforms") || "[]"); }
  catch { return []; }
}
const allowedPlatforms = new Set(getAllowedPlatforms());
const has = (p) => allowedPlatforms.has(p);

export const SHOULD_SHOW_DIGITAL_MARKETING = allowedPlatforms.size > 0;

// Per-platform menu — only platforms that ACTUALLY have a built page.
// Unbuilt platforms (Amazon Global, RK World, Myntra, Smytten, Meesho,
// Big Basket, Nykaa) are still defined in the backend ROLES map so role
// assignments are forward-compatible, but they don't appear in the sidebar
// until each one ships. Add the entry here in the same commit as the page.
const ALL_MENU = [
  { key: "amazon",          url: "/dashboard/digital-marketing/amazon",          icon: icons.IconShoppingCart, label: "Amazon" },
  { key: "flipkart",        url: "/dashboard/digital-marketing/flipkart",        icon: icons.IconBrandShopee,  label: "Flipkart" },
  { key: "zepto",           url: "/dashboard/digital-marketing/zepto",           icon: icons.IconBrandAndroid, label: "Zepto" },
  { key: "instamart",       url: "/dashboard/digital-marketing/instamart",       icon: icons.IconBrandAndroid, label: "Swiggy Instamart" },
  { key: "shopify",         url: "/dashboard/digital-marketing/shopify",         icon: icons.IconBrandStripe,  label: "Shopify" },
  { key: "influencers",     url: "/dashboard/digital-marketing/influencers",     icon: icons.IconStar,         label: "Influencers" },
  { key: "team_efficiency", url: "/dashboard/digital-marketing/team-efficiency", icon: icons.IconUsers,        label: "Team Efficiency" },
];

const children = SHOULD_SHOW_DIGITAL_MARKETING
  ? ALL_MENU.filter(m => has(m.key)).map(m => ({
      id:          `dm-${m.key}`,
      title:       <FormattedMessage id={m.label} defaultMessage={m.label} />,
      type:        "item",
      url:         m.url,
      icon:        m.icon,
      breadcrumbs: false,
    }))
  : [];

// Always append "B2B Leads ROI" + "Upload Reports" if the user has ANY platform access
if (SHOULD_SHOW_DIGITAL_MARKETING) {
  children.push({
    id: "dm-b2b-leads-roi",
    title: <FormattedMessage id="B2B Leads ROI" defaultMessage="B2B Leads ROI" />,
    type: "item",
    url: "/dashboard/digital-marketing/b2b-leads-roi",
    icon: icons.IconTarget,
    breadcrumbs: false,
  });
  children.push({
    id: "dm-upload",
    title: <FormattedMessage id="Upload Reports" />,
    type: "item",
    url: "/dashboard/digital-marketing/upload",
    icon: icons.IconFileUpload,
    breadcrumbs: false,
  });
  children.push({
    id: "dm-data-health",
    title: <FormattedMessage id="Data Health" defaultMessage="Data Health" />,
    type: "item",
    url: "/dashboard/digital-marketing/data-health",
    icon: icons.IconHeartbeat,
    breadcrumbs: false,
  });
  // ⚠️ NOT in ALL_MENU. That list is filtered by has(key) against the
  // backend's PLATFORM role map (amazon, flipkart, zepto...), and the Ad
  // Budget Advisor is not a platform — it reads Meta spend across every
  // campaign. Putting it there made the filter drop it silently: the code
  // compiled, the route worked, and the sidebar item simply never appeared.
  // Appended here with the other cross-platform tools instead.
  children.push({
    id: "dm-ad-advisor",
    title: <FormattedMessage id="Ad Budget Advisor" defaultMessage="Ad Budget Advisor" />,
    type: "item",
    url: "/dashboard/digital-marketing/ad-advisor",
    icon: icons.IconChartBar,
    breadcrumbs: false,
  });
}

export const DigitalMarketingTab = {
  id: "digital-marketing",
  title: <FormattedMessage id="Digital Marketing" />,
  type: "group",
  children:
    children.length > 0
      ? [
          {
            id: "dm-group-root",
            title: <FormattedMessage id="Digital Marketing" />,
            type: "collapse",
            icon: icons.IconChartBar,
            children,
          },
        ]
      : [],
};
